import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { A } from "@solidjs/router";
import type { Opportunity, OpportunityStage } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatDateTime, formatNumber, formatShare } from "../format.ts";
import { scope } from "../context.ts";
import { Icon } from "../icons.tsx";
import { simulatePower } from "../optimization.ts";
import { ErrorBox } from "../components/ErrorBox.tsx";
import {
  operator,
  PageHeader,
  Skeleton,
  useAction,
} from "../components/ui.tsx";

const FUNNEL: OpportunityStage[] = [
  "potential",
  "recommended",
  "approved",
  "inProgress",
  "verified",
];
const STAGES: OpportunityStage[] = [...FUNNEL, "rejected"];
const LINKS: Record<string, string> = {
  offHours: "/analysis",
  contractedPower: "/finance?tab=terms",
  reactive: "/overview",
  submetering: "/flows",
};

const zl = (v: number | null) =>
  v === null ? "—" : `${formatNumber(v, locale(), 0)} zł`;

/** The basis of an opportunity in words, with its figures formatted. */
function basis(o: Opportunity): string {
  const b = o.basis;
  const n = (k: string, d = 1) => formatNumber(b[k] ?? null, locale(), d);
  switch (o.kind) {
    case "offHours":
      return t("optimization.basis.offHours", {
        mean: n("meanKw"),
        base: n("baseKw"),
        work: n("workKw"),
        days: n("days", 0),
      });
    case "contractedPower":
      return t("optimization.basis.contractedPower", {
        contracted: n("contractedKw", 0),
        max: n("maxKw", 0),
        proposal: n("proposalKw", 0),
        days: n("days", 0),
      });
    case "reactive":
      return t("optimization.basis.reactive", {
        tanPhi: n("tanPhi", 2),
        threshold: n("threshold", 2),
        kvarh: n("excessKvarhPerYear", 0),
      });
    default:
      return t("optimization.basis.submetering", {
        share: formatShare(b.share ?? null, locale()),
        kwh: n("unmeteredKwh", 0),
        days: n("days", 0),
      });
  }
}

function Confidence(props: { n: number }) {
  return (
    <span
      class="conf"
      title={t("optimization.confidence", {
        level: t(`optimization.level.${props.n}`),
      })}
    >
      <For each={[1, 2, 3]}>
        {(i) => <i class={i <= props.n ? "on" : ""} />}
      </For>
    </span>
  );
}

const stageChip = (s: OpportunityStage) =>
  s === "verified"
    ? "ok"
    : s === "inProgress" || s === "approved"
      ? "acc"
      : s === "recommended"
        ? "warn"
        : "";

/**
 * Opportunities (prototype "Optymalizacje"): ways to save found in the place's data — load left on outside working
 * hours, contracted power above the peaks, reactive energy above the threshold, unmetered energy — with their
 * basis, estimated effect where terms are known, confidence, and the stage people moved them to. Estimates are not
 * added up: actions may overlap.
 */
export function OptimizationPage() {
  return (
    <>
      <PageHeader
        eyebrow={`${t("optimization.eyebrow")} · ${scope()?.name ?? ""}`}
        title={t("optimization.title")}
        sub={t("optimization.intro")}
      />
      <Show when={scope()} keyed>
        {(node) => <Opportunities nodeId={node.id} />}
      </Show>
    </>
  );
}

function Opportunities(props: { nodeId: string }) {
  const [list, { refetch }] = createResource(
    () => props.nodeId,
    (id) => api.optimization.list(id),
  );
  const [users] = createResource(() => api.events.users());
  const [selected, setSelected] = createSignal<string>();
  const current = () =>
    (list() ?? []).find((o) => o.kind === selected()) ?? list()?.[0];
  const count = (s: OpportunityStage) =>
    (list() ?? []).filter((o) => o.stage === s).length;
  const sum = (s: OpportunityStage) =>
    (list() ?? [])
      .filter((o) => o.stage === s && o.savingPerYear !== null)
      .reduce((a, o) => a + o.savingPerYear!, 0);
  const reload = () => {
    api.invalidate("/api/v1/nodes");
    void refetch();
  };

  return (
    <Show when={!list.error} fallback={<ErrorBox error={list.error} />}>
      <Show
        when={list()}
        fallback={
          <section class="card">
            <Skeleton lines={6} />
          </section>
        }
      >
        {(items) => (
          <>
            <div class="funnel">
              <For each={FUNNEL}>
                {(s) => (
                  <div>
                    <span class="eyebrow">{t(`optimization.stage.${s}`)}</span>
                    <b>{count(s)}</b>
                    <span class="q">
                      {sum(s) > 0
                        ? t("optimization.funnelSaving", { amount: zl(sum(s)) })
                        : t(`optimization.funnel.${s}`)}
                    </span>
                  </div>
                )}
              </For>
            </div>
            <Show
              when={items().length > 0}
              fallback={
                <section class="card empty">
                  <p class="muted" style={{ margin: 0 }}>
                    {t("optimization.none")}
                  </p>
                </section>
              }
            >
              <div class="grid g-2">
                <section class="card">
                  <div class="card-h">
                    <h2>{t("optimization.list")}</h2>
                  </div>
                  <div class="scrollx">
                    <table class="tbl">
                      <thead>
                        <tr>
                          <th>{t("optimization.action")}</th>
                          <th class="r">{t("optimization.effect")}</th>
                          <th>{t("optimization.sure")}</th>
                          <th>{t("optimization.stageLabel")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={items()}>
                          {(o) => (
                            <tr
                              class={`click${current()?.kind === o.kind ? " sel" : ""}`}
                              onClick={() => setSelected(o.kind)}
                            >
                              <td>
                                <b style={{ "font-weight": 500 }}>
                                  {t(`optimization.kind.${o.kind}`)}
                                </b>
                                <div class="hint">
                                  {o.detected
                                    ? basis(o)
                                    : t("optimization.notFound")}
                                </div>
                              </td>
                              <td class="r num">
                                {o.savingPerYear !== null
                                  ? t("optimization.perYear", {
                                      amount: zl(o.savingPerYear),
                                    })
                                  : o.energyKwhPerYear !== null
                                    ? `${formatNumber(o.energyKwhPerYear, locale(), 0)} kWh`
                                    : "—"}
                              </td>
                              <td>
                                <Show when={o.detected}>
                                  <Confidence n={o.confidence} />
                                </Show>
                              </td>
                              <td>
                                <span class={`chip ${stageChip(o.stage)}`}>
                                  {t(`optimization.stage.${o.stage}`)}
                                </span>
                              </td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </section>
                <Show when={current()} keyed>
                  {(o) => (
                    <Detail
                      o={o}
                      nodeId={props.nodeId}
                      users={(users() ?? []).map((u) => ({
                        id: u.id,
                        name: u.name,
                      }))}
                      onSaved={reload}
                    />
                  )}
                </Show>
              </div>
            </Show>
            <p class="hint" style={{ margin: 0 }}>
              {t("optimization.hint")}
            </p>
          </>
        )}
      </Show>
    </Show>
  );
}

function Detail(props: {
  o: Opportunity;
  nodeId: string;
  users: { id: string; name: string }[];
  onSaved: () => void;
}) {
  const o = () => props.o;
  const [stage, setStage] = createSignal<OpportunityStage>(props.o.stage);
  const [owner, setOwner] = createSignal(props.o.owner ?? "");
  const [note, setNote] = createSignal(props.o.note ?? "");
  const [run, error, busy] = useAction();
  const changed = () =>
    stage() !== o().stage ||
    (owner() || null) !== o().owner ||
    (note().trim() || null) !== o().note;
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t(`optimization.kind.${o().kind}`)}</h2>
        <span class="meta">{t(`optimization.category.${o().kind}`)}</span>
      </div>
      <Show when={o().kind === "contractedPower" && o().detected}>
        <PowerSimulator o={o()} />
      </Show>
      <div class="card-b">
        <dl class="kv" style={{ margin: 0 }}>
          <dt>{t("optimization.basisLabel")}</dt>
          <dd>{o().detected ? basis(o()) : t("optimization.notFound")}</dd>
          <dt>{t("optimization.effect")}</dt>
          <dd>
            {o().savingPerYear !== null
              ? t("optimization.perYear", { amount: zl(o().savingPerYear) })
              : o().energyKwhPerYear !== null
                ? t("optimization.energyOnly", {
                    kwh: formatNumber(o().energyKwhPerYear, locale(), 0),
                  })
                : t(`optimization.noSaving.${o().kind}`)}
          </dd>
          <dt>{t("optimization.sure")}</dt>
          <dd>
            <Confidence n={o().confidence} />{" "}
            {t(`optimization.confidenceWhy.${o().kind}`)}
          </dd>
          <dt>{t("optimization.verification")}</dt>
          <dd>{t("optimization.verificationText")}</dd>
          <Show when={o().updatedBy}>
            <dt>{t("optimization.lastChange")}</dt>
            <dd>
              {o().updatedBy} · {formatDateTime(o().updatedAt, locale())}
            </dd>
          </Show>
        </dl>
        <div
          style={{
            display: "flex",
            gap: "8px",
            "margin-top": "12px",
            "flex-wrap": "wrap",
          }}
        >
          <A class="btn" href={LINKS[o().kind] ?? "/overview"}>
            <Icon name="gauge" />
            {t(`optimization.open.${o().kind}`)}
          </A>
        </div>
      </div>
      <Show when={operator()}>
        <div
          class="card-b"
          style={{
            display: "grid",
            gap: "10px",
            "border-top": "1px solid var(--line)",
          }}
        >
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="opStage">{t("optimization.stageLabel")}</label>
              <select
                id="opStage"
                value={stage()}
                onChange={(e) =>
                  setStage(e.currentTarget.value as OpportunityStage)
                }
              >
                <For each={STAGES}>
                  {(s) => (
                    <option value={s}>{t(`optimization.stage.${s}`)}</option>
                  )}
                </For>
              </select>
            </div>
            <div class="field">
              <label for="opOwner">{t("optimization.owner")}</label>
              <select
                id="opOwner"
                value={owner()}
                onChange={(e) => setOwner(e.currentTarget.value)}
              >
                <option value="">—</option>
                <For each={props.users}>
                  {(u) => <option value={u.name}>{u.name}</option>}
                </For>
              </select>
            </div>
          </div>
          <div class="field">
            <label for="opNote">{t("optimization.note")}</label>
            <textarea
              id="opNote"
              rows={2}
              maxlength={1000}
              value={note()}
              onInput={(e) => setNote(e.currentTarget.value)}
            />
          </div>
          <div>
            <button
              type="button"
              class="btn primary"
              disabled={busy() || !changed()}
              onClick={() =>
                void run(async () => {
                  await api.optimization.set(props.nodeId, o().kind, {
                    stage: stage(),
                    owner: owner() || null,
                    note: note().trim() || null,
                  });
                  props.onSaved();
                })
              }
            >
              {t("common.save")}
            </button>
          </div>
          <Show when={error()}>
            <ErrorBox error={error()} />
          </Show>
        </div>
      </Show>
    </section>
  );
}

/** Try a contracted power against the monthly peaks (prototype slider): saving, months over it, net effect. */
function PowerSimulator(props: { o: Opportunity }) {
  const b = () => props.o.basis;
  const current = () => b().contractedKw ?? 0;
  const [proposed, setProposed] = createSignal(b().proposalKw ?? current());
  const peaks = () => props.o.peaks.map((p) => p.kw);
  const sim = createMemo(() =>
    simulatePower(
      peaks(),
      current(),
      proposed(),
      b().ratePerKwMonth ?? 0,
      b().penaltyPerKw ?? undefined,
    ),
  );
  const W = 520;
  const H = 200;
  const max = () => Math.max(current(), ...peaks()) * 1.1;
  const y = (v: number) => H - 26 - ((H - 36) * v) / max();
  const bw = () => (W - 50) / Math.max(1, peaks().length);
  const month = (iso: string) =>
    new Intl.DateTimeFormat(locale(), { month: "short" }).format(new Date(iso));
  return (
    <div class="card-b" style={{ display: "grid", gap: "12px" }}>
      <div
        style={{
          display: "flex",
          gap: "12px",
          "align-items": "center",
          "flex-wrap": "wrap",
        }}
      >
        <label for="pmoc" class="eyebrow">
          {t("optimization.power.proposed")}
        </label>
        <input
          id="pmoc"
          type="range"
          min={Math.max(5, Math.floor((Math.max(0, ...peaks()) * 0.8) / 5) * 5)}
          max={current()}
          step="5"
          value={proposed()}
          style={{ flex: "1", "min-width": "160px" }}
          onInput={(e) => setProposed(Number(e.currentTarget.value))}
        />
        <b class="num" style={{ "font-size": "16px" }}>
          {proposed()} kW
        </b>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", display: "block" }}
        role="img"
        aria-label={t("optimization.power.chart")}
      >
        <For each={props.o.peaks}>
          {(p, i) => (
            <>
              <rect
                x={40 + i() * bw() + bw() * 0.15}
                width={bw() * 0.7}
                y={y(p.kw)}
                height={H - 26 - y(p.kw)}
                fill={p.kw > proposed() ? "var(--crit)" : "var(--m-el)"}
                opacity="0.85"
              />
              <text
                x={40 + i() * bw() + bw() / 2}
                y={H - 10}
                text-anchor="middle"
                style={{ "font-size": "10px", fill: "var(--muted)" }}
              >
                {month(p.month)}
              </text>
              <text
                x={40 + i() * bw() + bw() / 2}
                y={y(p.kw) - 4}
                text-anchor="middle"
                style={{ "font-size": "10px", fill: "var(--muted)" }}
              >
                {formatNumber(p.kw, locale(), 0)}
              </text>
            </>
          )}
        </For>
        <line
          x1="36"
          x2={W}
          y1={y(current())}
          y2={y(current())}
          stroke="var(--faint)"
          stroke-dasharray="5 4"
        />
        <text
          x={W - 4}
          y={y(current()) - 4}
          text-anchor="end"
          style={{ "font-size": "10px", fill: "var(--muted)" }}
        >
          {t("optimization.power.now", {
            kw: formatNumber(current(), locale(), 0),
          })}
        </text>
        <line
          x1="36"
          x2={W}
          y1={y(proposed())}
          y2={y(proposed())}
          stroke="var(--accent)"
          stroke-width="2"
        />
        <text
          x={W - 4}
          y={y(proposed()) + 12}
          text-anchor="end"
          style={{ "font-size": "10px", fill: "var(--accent)" }}
        >
          {t("optimization.power.proposal", { kw: proposed() })}
        </text>
      </svg>
      <div class="axes">
        <div class="axis">
          <span class="eyebrow">{t("optimization.power.saving")}</span>
          <span class="num" style={{ "font-size": "16px" }}>
            {t("optimization.perYear", { amount: zl(sim().saving) })}
          </span>
        </div>
        <div class="axis">
          <span class="eyebrow">{t("optimization.power.over")}</span>
          <span class="num" style={{ "font-size": "16px" }}>
            {sim().over.length}
          </span>
        </div>
        <div class="axis">
          <span class="eyebrow">{t("optimization.power.penalties")}</span>
          <span class="num" style={{ "font-size": "16px" }}>
            {zl(sim().penalties)}
          </span>
        </div>
        <div class="axis">
          <span class="eyebrow">{t("optimization.power.net")}</span>
          <span
            class="num"
            style={{
              "font-size": "16px",
              color: sim().net > 0 ? "var(--ok)" : "var(--crit)",
            }}
          >
            {t("optimization.perYear", { amount: zl(sim().net) })}
          </span>
        </div>
      </div>
      <p class="hint" style={{ margin: 0 }}>
        {t(
          b().penaltyFromContract
            ? "optimization.power.hintContract"
            : "optimization.power.hint",
          {
            rate: formatNumber(b().ratePerKwMonth ?? 0, locale(), 2),
            penalty: formatNumber(
              b().penaltyPerKw ?? (b().ratePerKwMonth ?? 0) * 10,
              locale(),
              2,
            ),
            days: formatNumber(b().days ?? 0, locale(), 0),
          },
        )}
      </p>
    </div>
  );
}
